import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Empty,
  InputNumber,
  Modal,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { getAllEnableCities } from '../../server/countries/countriesServer';
import {
  cancelEnrichmentJob,
  enrichAllCities,
  enrichCity,
  fixCityAddresses,
  getEnrichmentJob,
  isTerminalStatus,
  listEnrichmentJobs,
} from '../../server/scraper/enrichmentServer';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

// Defaults must match backend EnrichmentOrchestratorService.DEFAULTS so the UI
// reflects what will actually run if a toggle isn't touched.
const DEFAULT_OPTIONS = {
  includeClubs: true,
  includePromoters: true,
  includeArtistsAndEvents: true,
  includeUpcoming: true,
  includePast: false,
  maxVenues: 500,
  maxEventsPerVenue: 30,
  limit: 500,
  polishWithGpt: true,
  scrapeInstagram: true,
  markVerified: true,
  minEventsThisYear: 1,
  // Event-first upcoming sweep. OFF for Full Enrichment; turned ON by the
  // Refresh preset. When on, the upcoming pass pulls EVERY event in the city
  // from RA and creates venues on demand, instead of only walking clubs we
  // already have.
  eventFirst: false,
  // How many months ahead the city-wide (event-first) sweep looks. Only used
  // when eventFirst is on. Backend clamps to 1–24.
  upcomingMonths: 6,
  dryRun: false,
};

// Presets are pure client-side option bundles — the backend orchestrator
// already supports every combination via includeClubs / includePromoters /
// includeArtistsAndEvents. Selecting a preset just calls setOptions().
//
// - fullEnrichment: original one-click flow — walk RA for new clubs +
//   promoters in the city, then walk every (existing + new) club/promoter
//   for events + artists. Heaviest run; use for cold-start or periodic deep
//   sync.
// - refreshEventsOnly: EVENT-FIRST city-wide upcoming sweep. Asks RA for every
//   upcoming event in the city (next 6 months) and, per event, creates the
//   venue's club on demand if we don't have it, then upserts the event and
//   enriches + links artists. Skips the separate club/promoter discovery
//   passes. This is the "never miss an upcoming event" loop — it catches
//   events at venues the discovery pass would skip (filtered, capped, or not
//   listed by RA as a venue at all).
const PRESETS = {
  fullEnrichment: {
    label: 'Full Enrichment',
    description:
      'Discover new clubs + promoters from RA, then scrape events and artists for all of them. Heaviest run.',
    options: { ...DEFAULT_OPTIONS },
  },
  refreshEventsOnly: {
    label: 'Refresh Upcoming Events (city-wide)',
    description:
      "Pulls EVERY upcoming event in the city from RA (next 6 months) and creates the venue's club on demand when it's missing — so no upcoming event is ever missed. Skips club/promoter discovery.",
    options: {
      ...DEFAULT_OPTIONS,
      includeClubs: false,
      includePromoters: false,
      includeArtistsAndEvents: true,
      includeUpcoming: true,
      includePast: false,
      eventFirst: true,
    },
  },
};

const STATUS_COLORS = {
  pending: 'default',
  running: 'processing',
  completed: 'success',
  failed: 'error',
  cancelling: 'warning',
  cancelled: 'default',
  skipped: 'default',
  // `interrupted` = gateway restarted mid-run. Amber, not red — DB writes
  // up to that point are preserved and a re-run is safe (idempotent).
  interrupted: 'warning',
};

const STATUS_ICONS = {
  pending: <ClockCircleOutlined />,
  running: <LoadingOutlined spin />,
  completed: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  cancelling: <StopOutlined />,
  cancelled: <StopOutlined />,
  skipped: <ClockCircleOutlined />,
  interrupted: <StopOutlined />,
};

const StatusTag = ({ status }) => (
  <Tag icon={STATUS_ICONS[status]} color={STATUS_COLORS[status] || 'default'}>
    {status}
  </Tag>
);

const POLL_INTERVAL_MS = 3000;

const EnrichmentTab = () => {
  // Cities
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  // Selection mode + selected city ids
  const [mode, setMode] = useState('single'); // 'single' | 'all' | 'subset'
  const [selectedCityIds, setSelectedCityIds] = useState([]);

  // Options
  const [options, setOptions] = useState(DEFAULT_OPTIONS);

  // Which preset (if any) is currently active. Derived purely from the
  // options state — if the user manually flips a switch and breaks the
  // preset match, `activePreset` becomes null and neither preset button is
  // highlighted (signaling "custom"). Only the pipeline-relevant fields are
  // compared; numeric tunables like maxVenues / limit don't disqualify a
  // preset match, since those don't change what the run does conceptually.
  const PRESET_MATCH_KEYS = [
    'includeClubs',
    'includePromoters',
    'includeArtistsAndEvents',
    'includeUpcoming',
    'includePast',
    'eventFirst',
  ];
  const activePreset = useMemo(() => {
    for (const [key, preset] of Object.entries(PRESETS)) {
      const matches = PRESET_MATCH_KEYS.every(
        (k) => options[k] === preset.options[k],
      );
      if (matches) return key;
    }
    return null;
  }, [options]);

  // Job state
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Recent jobs list (for the panel under the job view)
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  // Fix city addresses (surgical heal — pulls fresh addresses from RA, Mapbox
  // geocodes when coords are bad, propagates onto upcoming events). Never
  // creates anything. Independent from the enrichment job flow.
  const [fixAddressesLoading, setFixAddressesLoading] = useState(false);
  const [fixAddressesForce, setFixAddressesForce] = useState(false);
  const [lastFixAddressesResult, setLastFixAddressesResult] = useState(null);

  const pollRef = useRef(null);

  // -----------------------------------------------------------------------
  // City loading
  // -----------------------------------------------------------------------
  const loadCities = useCallback(async () => {
    setCitiesLoading(true);
    try {
      const res = await getAllEnableCities();
      const list = res?.data ?? res ?? [];
      // Normalize: ensure we have _id + name + optional country.name
      const normalized = (Array.isArray(list) ? list : list?.data ?? [])
        .filter((c) => c && (c._id || c.id))
        .map((c) => ({
          _id: String(c._id || c.id),
          name: c.name,
          country: c?.country?.name || c?.country?.code || null,
        }));
      normalized.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(normalized);
    } catch (err) {
      console.error('Failed to load cities:', err);
      message.error('Failed to load cities — falling back to manual ObjectId entry');
    } finally {
      setCitiesLoading(false);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Recent jobs
  // -----------------------------------------------------------------------
  const loadRecentJobs = useCallback(async () => {
    setRecentLoading(true);
    try {
      const res = await listEnrichmentJobs({ limit: 10 });
      setRecentJobs(res?.items || []);
    } catch (err) {
      console.error('Failed to load recent jobs:', err);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCities();
    loadRecentJobs();
  }, [loadCities, loadRecentJobs]);

  // -----------------------------------------------------------------------
  // Polling
  // -----------------------------------------------------------------------
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollJob = useCallback(
    async (jobId) => {
      try {
        const job = await getEnrichmentJob(jobId);
        setCurrentJob(job);
        if (isTerminalStatus(job?.status)) {
          stopPolling();
          // Refresh the recent jobs list once we land on a terminal state.
          loadRecentJobs();
          if (job.status === 'completed') {
            message.success(
              `Enrichment completed — ${job.totals?.eventsCreated || 0} events created, ${job.totals?.artistsCreated || 0} artists`,
            );
          } else if (job.status === 'failed') {
            message.error(`Enrichment failed: ${job.error || 'unknown error'}`);
          } else if (job.status === 'cancelled') {
            message.warning('Enrichment cancelled');
          } else if (job.status === 'interrupted') {
            // Gateway restarted mid-run. Totals already in DB are real; the
            // operator just needs to re-run to reconcile what wasn't accounted
            // for. Use warning (amber) — not error — to convey that.
            message.warning(
              'Enrichment interrupted by an api-gateway restart. Partial work is already in the DB. Re-run the same job to reconcile (it is idempotent).',
              8,
            );
          }
        }
      } catch (err) {
        console.error('Polling failed:', err);
      }
    },
    [stopPolling, loadRecentJobs],
  );

  const startPolling = useCallback(
    (jobId) => {
      stopPolling();
      pollJob(jobId);
      pollRef.current = setInterval(() => pollJob(jobId), POLL_INTERVAL_MS);
    },
    [pollJob, stopPolling],
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    if (currentJobId) startPolling(currentJobId);
  }, [currentJobId, startPolling]);

  // -----------------------------------------------------------------------
  // Run handlers
  // -----------------------------------------------------------------------
  const buildPayload = () => {
    // Strip values equal to defaults so the backend payload stays minimal.
    const out = {};
    for (const [k, v] of Object.entries(options)) {
      if (v !== DEFAULT_OPTIONS[k]) out[k] = v;
    }
    return out;
  };

  const handleRun = async () => {
    if (mode === 'single' && selectedCityIds.length !== 1) {
      message.warning('Pick exactly one city for the single-city run');
      return;
    }
    if (mode === 'subset' && selectedCityIds.length === 0) {
      message.warning('Pick at least one city for a subset run');
      return;
    }

    // Build the actual pipeline preview from the current toggles so the modal
    // shows what WILL run, not a hardcoded fiction.
    const steps = [];
    if (options.includeClubs) steps.push('clubs');
    if (options.includePromoters) steps.push('promoters');
    if (options.includeArtistsAndEvents) {
      const passes = [];
      if (options.includeUpcoming) {
        passes.push(
          options.eventFirst ? 'upcoming (city-wide sweep)' : 'upcoming',
        );
      }
      if (options.includePast) passes.push('past');
      if (passes.length) steps.push(`events+artists (${passes.join(' + ')})`);
    }
    const pipelinePreview = steps.length
      ? steps.join(' → ')
      : 'NOTHING (every step is disabled)';

    // Title varies by preset so the operator sees what they're actually
    // committing to. Falls back to "enrichment" wording when the options
    // don't match any preset (custom configuration).
    const presetLabel =
      activePreset && PRESETS[activePreset]?.label
        ? PRESETS[activePreset].label.toLowerCase()
        : 'enrichment';
    Modal.confirm({
      title:
        mode === 'single'
          ? `Run ${presetLabel} for this city?`
          : mode === 'subset'
          ? `Run ${presetLabel} for ${selectedCityIds.length} selected cities?`
          : `Run ${presetLabel} for ALL RA-mapped cities?`,
      content: (
        <div>
          <p>
            Pipeline: <strong>{pipelinePreview}</strong>
          </p>
          {steps.length === 0 && (
            <Alert
              type="warning"
              showIcon
              message="No steps selected. Enable at least one of: Import clubs, Import promoters, Import events + artists."
              style={{ marginBottom: 12 }}
            />
          )}
          <p>
            Past events:{' '}
            <Tag color={options.includePast ? 'orange' : 'default'}>
              {options.includePast ? 'INCLUDED' : 'skipped'}
            </Tag>
            {options.includePast && (
              <Text type="warning"> Past-events runs are 5–10× slower.</Text>
            )}
          </p>
          {options.dryRun && (
            <p>
              <Tag color="purple">DRY RUN</Tag> No DB writes — counts only.
            </p>
          )}
          <p>The job runs in the background; you can close this tab and come back to check progress.</p>
        </div>
      ),
      okButtonProps: { disabled: steps.length === 0 },
      okText: 'Start',
      cancelText: 'Cancel',
      onOk: async () => {
        setSubmitting(true);
        try {
          let res;
          const payload = buildPayload();
          if (mode === 'single') {
            res = await enrichCity({ ...payload, cityId: selectedCityIds[0] });
          } else if (mode === 'subset') {
            res = await enrichAllCities({ ...payload, cityIds: selectedCityIds });
          } else {
            res = await enrichAllCities(payload);
          }
          if (res?.jobId) {
            setCurrentJobId(res.jobId);
            message.success(`Job queued: ${res.jobId} (${res.cities} cities)`);
          } else {
            message.warning('Job created but no jobId returned. Check the recent jobs list.');
            loadRecentJobs();
          }
        } catch (err) {
          console.error('Failed to start enrichment:', err);
          message.error(
            err?.response?.data?.message ||
              err?.message ||
              'Failed to start enrichment job',
          );
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const handleCancel = async () => {
    if (!currentJobId) return;
    Modal.confirm({
      title: 'Cancel this enrichment job?',
      content:
        'The orchestrator will stop at the next clean boundary (between steps or cities). In-flight requests to RA finish first. Partial DB writes are kept.',
      okText: 'Yes, cancel',
      cancelText: 'Keep running',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await cancelEnrichmentJob(currentJobId);
          message.warning('Cancellation requested');
        } catch (err) {
          console.error('Failed to cancel:', err);
          message.error('Failed to request cancellation');
        }
      },
    });
  };

  const handleOpenExistingJob = (jobId) => {
    setCurrentJobId(jobId);
  };

  const handleClearJob = () => {
    stopPolling();
    setCurrentJobId(null);
    setCurrentJob(null);
  };

  // -----------------------------------------------------------------------
  // Fix city addresses (one-shot surgical heal — no creates)
  // -----------------------------------------------------------------------
  // Re-uses the existing single-city selection rather than a separate picker.
  const fixAddressesCityId =
    mode === 'single' && selectedCityIds.length === 1 ? selectedCityIds[0] : null;

  const handleFixCityAddresses = async () => {
    if (!fixAddressesCityId) {
      message.warning('Pick a single city first to fix its addresses');
      return;
    }
    Modal.confirm({
      title: 'Fix addresses for this city?',
      content: (
        <div>
          <p>
            <strong>Step 1.</strong> Walks every Event-Manager profile in this
            city, pulls a fresh address from RA per stored scrapSource, and
            patches the profile when its address is missing, looks like a bare
            label, mentions a foreign country, or has bad/integer-rounded
            coordinates. Falls back to Mapbox geocoding when RA's coordinates
            are coarse.
          </p>
          <p>
            <strong>Step 2.</strong> For every UPCOMING event whose creater is
            one of those profiles, copies the now-correct address onto the
            event.
          </p>
          <p>
            <strong>Creates nothing.</strong> Safe to re-run.{' '}
            {fixAddressesForce && (
              <Tag color="orange">forceRefresh ON — re-pull every profile and overwrite every upcoming event</Tag>
            )}
          </p>
        </div>
      ),
      okText: 'Run fix',
      cancelText: 'Cancel',
      onOk: async () => {
        setFixAddressesLoading(true);
        try {
          const res = await fixCityAddresses({
            cityId: fixAddressesCityId,
            forceRefresh: fixAddressesForce,
          });
          setLastFixAddressesResult(res);
          const p = res?.profiles;
          const e = res?.events;
          message.success(
            `Profiles: ${p?.patched ?? 0}/${p?.scanned ?? 0} patched · Upcoming events: ${e?.eventsPatched ?? 0}/${e?.eventsScanned ?? 0} patched`,
          );
        } catch (err) {
          console.error('Fix city addresses failed:', err);
          message.error(
            err?.response?.data?.message || err?.message || 'Fix city addresses failed',
          );
        } finally {
          setFixAddressesLoading(false);
        }
      },
    });
  };

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------
  const cityOptions = useMemo(
    () =>
      cities.map((c) => ({
        value: c._id,
        label: c.country ? `${c.name} — ${c.country}` : c.name,
      })),
    [cities],
  );

  // Cities grouped by country, for the "Pick cities" mode. Powers both the
  // grouped dropdown and the quick-add-by-country picker.
  const countryGroups = useMemo(() => {
    const map = new Map();
    for (const c of cities) {
      const country = c.country || 'Other';
      if (!map.has(country)) map.set(country, []);
      map.get(country).push(c);
    }
    return Array.from(map.entries())
      .map(([country, list]) => ({
        country,
        cityIds: list.map((c) => c._id),
        cities: list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      }))
      .sort((a, b) => a.country.localeCompare(b.country));
  }, [cities]);

  // Grouped (optgroup) options for the multi-select dropdown.
  const groupedCityOptions = useMemo(
    () =>
      countryGroups.map((g) => ({
        label: `${g.country} (${g.cities.length})`,
        options: g.cities.map((c) => ({ value: c._id, label: c.name })),
      })),
    [countryGroups],
  );

  // Add every active city of the given country to the current selection.
  const addCountryToSelection = useCallback(
    (country) => {
      const g = countryGroups.find((x) => x.country === country);
      if (!g) return;
      setSelectedCityIds((prev) => Array.from(new Set([...prev, ...g.cityIds])));
    },
    [countryGroups],
  );

  const stepColumns = [
    {
      title: 'Step',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (n) => <Tag>{n}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (s) => <StatusTag status={s} />,
    },
    {
      title: 'Summary',
      key: 'summary',
      render: (_, step) => {
        if (step.error) {
          return <Text type="danger">{step.error}</Text>;
        }
        const r = step.result;
        if (!r) return <Text type="secondary">—</Text>;
        if (step.name === 'clubs' || step.name === 'promoters') {
          return (
            <span>
              created <strong>{r.created || 0}</strong> · skipped{' '}
              <strong>{r.skipped?.length || 0}</strong> · errors{' '}
              <strong>{r.errors?.length || 0}</strong>
            </span>
          );
        }
        if (step.name === 'events-artists') {
          const sumPass = (p) =>
            p
              ? `events=${p.eventsCreated || 0} (existed ${p.eventsAlreadyExisted || 0}) · artists=${p.artistsCreated || 0} · fails=${p.failures?.length || 0}`
              : null;
          return (
            <Space direction="vertical" size={0}>
              {r.upcoming && (
                <Text>
                  <Tag color="blue">upcoming</Tag> {sumPass(r.upcoming)}
                </Text>
              )}
              {r.past && (
                <Text>
                  <Tag color="orange">past</Tag> {sumPass(r.past)}
                </Text>
              )}
            </Space>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
  ];

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------
  const showSingleCityPicker = mode === 'single';
  const showSubsetPicker = mode === 'subset';

  return (
    <div className="enrichment-tab">
      <Alert
        type="info"
        showIcon
        message="Full RA Enrichment — one click per city, or all cities at once"
        description={
          <span>
            Runs <strong>Clubs → Promoters → Events & Artists</strong> in the correct
            order. Past events are OPT-IN. Jobs run in the background; close the
            tab and come back — progress is persisted in MongoDB.
          </span>
        }
        className="mb-4"
      />

      <Row gutter={[16, 16]}>
        {/* LEFT COLUMN — controls */}
        <Col xs={24} lg={10}>
          <Card title="What to enrich" size="small">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Segmented
                block
                options={[
                  { label: 'Single city', value: 'single' },
                  { label: 'Pick cities', value: 'subset' },
                  { label: 'All RA-mapped', value: 'all' },
                ]}
                value={mode}
                onChange={(v) => {
                  setMode(v);
                  setSelectedCityIds([]);
                }}
              />

              {showSingleCityPicker && (
                <Select
                  showSearch
                  placeholder="Select a city"
                  loading={citiesLoading}
                  value={selectedCityIds[0]}
                  onChange={(v) => setSelectedCityIds([v])}
                  options={cityOptions}
                  filterOption={(input, option) =>
                    (option?.label || '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  style={{ width: '100%' }}
                  allowClear
                />
              )}

              {showSubsetPicker && (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Space wrap>
                    <Button
                      size="small"
                      onClick={() =>
                        setSelectedCityIds(cityOptions.map((o) => o.value))
                      }
                      disabled={citiesLoading || cityOptions.length === 0}
                    >
                      Select all active ({cityOptions.length})
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setSelectedCityIds([])}
                      disabled={selectedCityIds.length === 0}
                    >
                      Clear
                    </Button>
                    {selectedCityIds.length > 0 && (
                      <Text type="secondary">
                        {selectedCityIds.length} selected
                      </Text>
                    )}
                  </Space>
                  {/* Quick-add whole countries. Selecting one drops in all its
                      active cities; it acts as an action picker (no persisted
                      tags) so you can keep adding countries. */}
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Quick add by country (e.g. Canada, United States)…"
                    value={[]}
                    onSelect={(country) => addCountryToSelection(country)}
                    options={countryGroups.map((g) => ({
                      value: g.country,
                      label: `${g.country} (${g.cities.length})`,
                    }))}
                    filterOption={(input, option) =>
                      (option?.label || '')
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                    maxTagCount={0}
                    maxTagPlaceholder={() => 'pick a country to add its cities'}
                  />
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="Select one or more cities"
                    loading={citiesLoading}
                    value={selectedCityIds}
                    onChange={setSelectedCityIds}
                    options={groupedCityOptions}
                    filterOption={(input, option) =>
                      (option?.label || '')
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                    maxTagCount="responsive"
                  />
                </Space>
              )}

              {mode === 'all' && (
                <Alert
                  type="warning"
                  showIcon
                  message="Will run on every City doc that matches an entry in city-to-ra-area.ts (~80 cities)."
                />
              )}
            </Space>
          </Card>

          <Card title="Options" size="small" className="mt-4">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Space wrap>
                  {Object.entries(PRESETS).map(([key, preset]) => (
                    <Button
                      key={key}
                      type={activePreset === key ? 'primary' : 'default'}
                      icon={
                        key === 'fullEnrichment' ? (
                          <ThunderboltOutlined />
                        ) : (
                          <ReloadOutlined />
                        )
                      }
                      onClick={() =>
                        setOptions((o) => ({ ...o, ...preset.options }))
                      }
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Space>
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                  {activePreset
                    ? PRESETS[activePreset].description
                    : 'Custom configuration — toggles below override any preset.'}
                </Paragraph>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <Row align="middle" justify="space-between">
                <Col><Text>Import clubs</Text></Col>
                <Col>
                  <Switch
                    checked={options.includeClubs}
                    onChange={(v) => setOptions((o) => ({ ...o, includeClubs: v }))}
                  />
                </Col>
              </Row>
              <Row align="middle" justify="space-between">
                <Col><Text>Import promoters</Text></Col>
                <Col>
                  <Switch
                    checked={options.includePromoters}
                    onChange={(v) => setOptions((o) => ({ ...o, includePromoters: v }))}
                  />
                </Col>
              </Row>
              <Row align="middle" justify="space-between">
                <Col><Text>Import events + artists</Text></Col>
                <Col>
                  <Switch
                    checked={options.includeArtistsAndEvents}
                    onChange={(v) =>
                      setOptions((o) => ({ ...o, includeArtistsAndEvents: v }))
                    }
                  />
                </Col>
              </Row>

              <Divider style={{ margin: '8px 0' }} />

              <Row align="middle" justify="space-between">
                <Col>
                  <Text>Include upcoming events</Text>{' '}
                  <Tooltip title="The 'all upcoming events in one click' knob — keeps your DB updated">
                    <ExclamationCircleOutlined />
                  </Tooltip>
                </Col>
                <Col>
                  <Switch
                    checked={options.includeUpcoming}
                    onChange={(v) => setOptions((o) => ({ ...o, includeUpcoming: v }))}
                  />
                </Col>
              </Row>
              <Row align="middle" justify="space-between">
                <Col>
                  <Text>Include past events</Text>{' '}
                  <Tag color="orange">heavy</Tag>
                </Col>
                <Col>
                  <Switch
                    checked={options.includePast}
                    onChange={(v) => setOptions((o) => ({ ...o, includePast: v }))}
                  />
                </Col>
              </Row>

              <Collapse ghost className="mt-2">
                <Panel header="Advanced (limits + flags)" key="adv">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Row align="middle" justify="space-between">
                      <Col><Text>Polish bios with GPT</Text></Col>
                      <Col>
                        <Switch
                          checked={options.polishWithGpt}
                          onChange={(v) => setOptions((o) => ({ ...o, polishWithGpt: v }))}
                        />
                      </Col>
                    </Row>
                    <Row align="middle" justify="space-between">
                      <Col><Text>Scrape Instagram</Text></Col>
                      <Col>
                        <Switch
                          checked={options.scrapeInstagram}
                          onChange={(v) => setOptions((o) => ({ ...o, scrapeInstagram: v }))}
                        />
                      </Col>
                    </Row>
                    <Row align="middle" justify="space-between">
                      <Col><Text>Mark new profiles as verified</Text></Col>
                      <Col>
                        <Switch
                          checked={options.markVerified}
                          onChange={(v) => setOptions((o) => ({ ...o, markVerified: v }))}
                        />
                      </Col>
                    </Row>
                    <Row align="middle" justify="space-between">
                      <Col>
                        <Text>Dry run</Text>{' '}
                        <Tooltip title="Simulate — no DB writes, just counts">
                          <ExclamationCircleOutlined />
                        </Tooltip>
                      </Col>
                      <Col>
                        <Switch
                          checked={options.dryRun}
                          onChange={(v) => setOptions((o) => ({ ...o, dryRun: v }))}
                        />
                      </Col>
                    </Row>

                    <Divider style={{ margin: '8px 0' }} />

                    <Row align="middle" justify="space-between" gutter={8}>
                      <Col><Text>Max venues to walk</Text></Col>
                      <Col>
                        <InputNumber
                          min={1}
                          max={1000}
                          value={options.maxVenues}
                          onChange={(v) =>
                            setOptions((o) => ({ ...o, maxVenues: v ?? DEFAULT_OPTIONS.maxVenues }))
                          }
                        />
                      </Col>
                    </Row>
                    <Row align="middle" justify="space-between" gutter={8}>
                      <Col><Text>Max events per venue</Text></Col>
                      <Col>
                        <InputNumber
                          min={1}
                          max={100}
                          value={options.maxEventsPerVenue}
                          onChange={(v) =>
                            setOptions((o) => ({
                              ...o,
                              maxEventsPerVenue:
                                v ?? DEFAULT_OPTIONS.maxEventsPerVenue,
                            }))
                          }
                        />
                      </Col>
                    </Row>
                    <Row align="middle" justify="space-between" gutter={8}>
                      <Col>
                        <Text>Upcoming window (months)</Text>{' '}
                        <Tooltip title="Only used by the city-wide sweep (Refresh Upcoming Events). How many months ahead to pull events from RA. 1–24.">
                          <ExclamationCircleOutlined />
                        </Tooltip>
                      </Col>
                      <Col>
                        <InputNumber
                          min={1}
                          max={24}
                          value={options.upcomingMonths}
                          disabled={!options.eventFirst}
                          onChange={(v) =>
                            setOptions((o) => ({
                              ...o,
                              upcomingMonths: v ?? DEFAULT_OPTIONS.upcomingMonths,
                            }))
                          }
                        />
                      </Col>
                    </Row>
                    <Row align="middle" justify="space-between" gutter={8}>
                      <Col><Text>RA list limit (clubs / promoters)</Text></Col>
                      <Col>
                        <InputNumber
                          min={1}
                          max={500}
                          value={options.limit}
                          onChange={(v) =>
                            setOptions((o) => ({ ...o, limit: v ?? DEFAULT_OPTIONS.limit }))
                          }
                        />
                      </Col>
                    </Row>
                    <Row align="middle" justify="space-between" gutter={8}>
                      <Col><Text>Min events this year</Text></Col>
                      <Col>
                        <InputNumber
                          min={0}
                          max={100}
                          value={options.minEventsThisYear}
                          onChange={(v) =>
                            setOptions((o) => ({
                              ...o,
                              minEventsThisYear:
                                v ?? DEFAULT_OPTIONS.minEventsThisYear,
                            }))
                          }
                        />
                      </Col>
                    </Row>
                  </Space>
                </Panel>
              </Collapse>
            </Space>
          </Card>

          <div className="mt-4">
            <Space>
              <Button
                type="primary"
                size="large"
                icon={<ThunderboltOutlined />}
                onClick={handleRun}
                loading={submitting}
                disabled={
                  (mode === 'single' && selectedCityIds.length !== 1) ||
                  (mode === 'subset' && selectedCityIds.length === 0)
                }
              >
                {mode === 'all'
                  ? 'Enrich ALL RA-mapped cities'
                  : mode === 'subset'
                  ? `Enrich ${selectedCityIds.length || ''} cit${selectedCityIds.length === 1 ? 'y' : 'ies'}`
                  : 'Enrich this city'}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadCities();
                  loadRecentJobs();
                }}
              >
                Refresh
              </Button>
            </Space>
          </div>

          {/* One-shot surgical fix — heals profile + upcoming-event addresses */}
          <Card
            title="Fix addresses (profiles + upcoming events)"
            size="small"
            className="mt-4"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message={
                  fixAddressesCityId
                    ? 'Operates on the city selected above (single-city mode).'
                    : 'Pick a single city above to enable the fix button.'
                }
                description="Pulls fresh addresses from RA for every EM profile in the city, falls back to Mapbox geocoding when coords are coarse/missing, then patches every UPCOMING event whose creater is one of those profiles. Creates nothing."
              />
              <Row align="middle" justify="space-between">
                <Col>
                  <Text>
                    Force refresh{' '}
                    <Tooltip title="Override heuristics: re-pull every profile from RA and overwrite every upcoming event, even those that already look fine.">
                      <ExclamationCircleOutlined />
                    </Tooltip>
                  </Text>
                </Col>
                <Col>
                  <Switch checked={fixAddressesForce} onChange={setFixAddressesForce} />
                </Col>
              </Row>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleFixCityAddresses}
                loading={fixAddressesLoading}
                disabled={!fixAddressesCityId}
              >
                Fix addresses for this city
              </Button>
              {lastFixAddressesResult && (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Alert
                    type="success"
                    message={
                      <span>
                        <strong>EM profiles:</strong>{' '}
                        patched {lastFixAddressesResult.profiles?.patched ?? 0} /{' '}
                        {lastFixAddressesResult.profiles?.scanned ?? 0}
                        {' · '}already good {lastFixAddressesResult.profiles?.alreadyGood ?? 0}
                        {(lastFixAddressesResult.profiles?.cityMismatchPatched ?? 0) > 0 && (
                          <>
                            {' · '}country-mismatch fixed{' '}
                            {lastFixAddressesResult.profiles?.cityMismatchPatched}
                          </>
                        )}
                        {' · '}errors {(lastFixAddressesResult.profiles?.errors || []).length}
                      </span>
                    }
                  />
                  <Alert
                    type="success"
                    message={
                      <span>
                        <strong>Upcoming events:</strong>{' '}
                        patched {lastFixAddressesResult.events?.eventsPatched ?? 0} /{' '}
                        {lastFixAddressesResult.events?.eventsScanned ?? 0}
                        {' · '}already good {lastFixAddressesResult.events?.eventsAlreadyGood ?? 0}
                        {(lastFixAddressesResult.events?.eventsCityMismatchPatched ?? 0) > 0 && (
                          <>
                            {' · '}country-mismatch fixed{' '}
                            {lastFixAddressesResult.events?.eventsCityMismatchPatched}
                          </>
                        )}
                        {' · '}errors {(lastFixAddressesResult.events?.errors || []).length}
                      </span>
                    }
                  />
                </Space>
              )}
            </Space>
          </Card>
        </Col>

        {/* RIGHT COLUMN — current job + recent jobs */}
        <Col xs={24} lg={14}>
          {currentJob ? (
            <JobDetail
              job={currentJob}
              onCancel={handleCancel}
              onClear={handleClearJob}
              stepColumns={stepColumns}
            />
          ) : (
            <Card title="Active Job" size="small">
              <Empty
                description="No active job. Pick a city and hit Enrich, or open a recent job below."
              />
            </Card>
          )}

          <Card
            title="Recent Jobs"
            size="small"
            className="mt-4"
            extra={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={loadRecentJobs}
                loading={recentLoading}
              >
                Refresh
              </Button>
            }
          >
            {recentJobs.length === 0 ? (
              <Empty description="No jobs yet" />
            ) : (
              <Table
                size="small"
                dataSource={recentJobs}
                rowKey="_id"
                pagination={false}
                columns={[
                  {
                    title: 'Type',
                    dataIndex: 'type',
                    width: 140,
                    render: (t) => <Tag>{t}</Tag>,
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    width: 130,
                    render: (s) => <StatusTag status={s} />,
                  },
                  {
                    title: 'Cities',
                    width: 90,
                    render: (_, j) =>
                      `${j.progress?.citiesDone || 0} / ${j.progress?.citiesTotal || j.cities?.length || 0}`,
                  },
                  {
                    title: 'Created / Existed / Artists',
                    render: (_, j) => (
                      <Text style={{ fontSize: 12 }}>
                        clubs={j.totals?.clubsCreated || 0} · promo={j.totals?.promotersCreated || 0} · ev={j.totals?.eventsCreated || 0} (existed {j.totals?.eventsAlreadyExisted || 0}) · art={j.totals?.artistsCreated || 0}
                      </Text>
                    ),
                  },
                  {
                    title: 'Started',
                    dataIndex: 'startedAt',
                    width: 140,
                    render: (d) => (d ? new Date(d).toLocaleString() : '—'),
                  },
                  {
                    title: '',
                    key: 'actions',
                    width: 90,
                    render: (_, j) => (
                      <Button
                        size="small"
                        type="link"
                        onClick={() => handleOpenExistingJob(j._id)}
                      >
                        View
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// =========================================================================
// Sub-component: live view of one job
// =========================================================================
const JobDetail = ({ job, onCancel, onClear, stepColumns }) => {
  const totalCities = job.progress?.citiesTotal || job.cities?.length || 0;
  const doneCities = job.progress?.citiesDone || 0;
  const percent = totalCities > 0 ? Math.round((doneCities / totalCities) * 100) : 0;
  const canCancel = job.status === 'pending' || job.status === 'running';

  return (
    <Card
      title={
        <Space>
          <PlayCircleOutlined />
          <span>Job</span>
          <Text code style={{ fontSize: 12 }}>{job._id}</Text>
          <StatusTag status={job.status} />
        </Space>
      }
      size="small"
      extra={
        <Space>
          {canCancel && (
            <Button size="small" danger icon={<StopOutlined />} onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button size="small" onClick={onClear}>
            Close
          </Button>
        </Space>
      }
    >
      <Row gutter={16}>
        <Col span={24}>
          <Progress
            percent={percent}
            status={
              job.status === 'failed'
                ? 'exception'
                : job.status === 'interrupted'
                ? 'normal'
                : job.status === 'cancelled' || job.status === 'cancelling'
                ? 'normal'
                : job.status === 'completed'
                ? 'success'
                : 'active'
            }
            format={() => `${doneCities} / ${totalCities} cities`}
          />
          {job.status === 'interrupted' && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Interrupted by an api-gateway restart"
              description={
                <span>
                  The orchestrator was killed mid-run (e.g. nest --watch
                  recompile, deploy). Profiles and events already inserted
                  before the restart are safe in the DB. <strong>Re-running
                  this job is safe and idempotent</strong> — RA-sourced rows
                  dedupe by source ID, so re-imported events will count under
                  <em> &nbsp;Existed</em> rather than <em>Events</em>.
                </span>
              }
            />
          )}
        </Col>
      </Row>

      <Row gutter={16} className="mt-4">
        <Col span={4}>
          <Statistic title="Clubs" value={job.totals?.clubsCreated || 0} />
        </Col>
        <Col span={4}>
          <Statistic title="Promoters" value={job.totals?.promotersCreated || 0} />
        </Col>
        <Col span={4}>
          <Statistic title="Events" value={job.totals?.eventsCreated || 0} />
        </Col>
        <Col span={4}>
          <Statistic title="Existed" value={job.totals?.eventsAlreadyExisted || 0} />
        </Col>
        <Col span={4}>
          <Statistic title="Artists" value={job.totals?.artistsCreated || 0} />
        </Col>
        <Col span={4}>
          <Statistic
            title="Errors"
            value={job.totals?.errors || 0}
            valueStyle={{ color: (job.totals?.errors || 0) > 0 ? '#cf1322' : undefined }}
          />
        </Col>
      </Row>

      {job.error && (
        <Alert
          type="error"
          showIcon
          message="Job error"
          description={job.error}
          className="mt-4"
        />
      )}

      <Divider />

      <Collapse defaultActiveKey={job.cities?.length === 1 ? ['c0'] : []}>
        {(job.cities || []).map((c, idx) => (
          <Panel
            key={`c${idx}`}
            header={
              <Space>
                <GlobalOutlined />
                <strong>{c.cityName}</strong>
                <StatusTag status={c.status} />
                {c.startedAt && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    started {new Date(c.startedAt).toLocaleTimeString()}
                  </Text>
                )}
              </Space>
            }
          >
            <Table
              size="small"
              dataSource={c.steps || []}
              rowKey="name"
              pagination={false}
              columns={stepColumns}
            />
          </Panel>
        ))}
      </Collapse>
    </Card>
  );
};

export default EnrichmentTab;
